import { ensureDefined } from "common/utils/assertOfType";

/**
 * A half-edge mesh for geometry processing. This data structure links edges of adjacent faces together,
 * so subdividing an edge can be propagated to neighboring faces.
 * @module
 */
import type { Mesh3, QuadMesh3 } from "./mesh";
import { quadMesh3New } from "./mesh";

interface Vertex {
  position: number[];
  index: number;
}

interface HalfEdge {
  originatingVertex: Vertex;
  twin: HalfEdge;
  face: HalfEdgeFace;

  otherVertex?: Vertex; // Only set for edges where the twin was not found.
  isNonManifold?: boolean; // Set if non-manifold edge is found and throwOnNonManifoldEdges is false.
}

interface HalfEdgeFace {
  halfEdges: HalfEdge[]; // In CCW order.
}

export interface HalfEdgeMesh {
  verts: Vertex[];
  faces: HalfEdgeFace[];
}

function halfEdgeLength(edge: HalfEdge): number {
  const vertA = edge.originatingVertex.position;
  const vertB = edge.twin.originatingVertex.position;
  const x = vertA[0] - vertB[0];
  const y = vertA[1] - vertB[1];
  const z = vertA[2] - vertB[2];
  return Math.sqrt(x * x + y * y + z * z);
}

function halfEdgeIdentifier(edge: HalfEdge): string {
  const vertIndexA = edge.originatingVertex.index;
  const vertIndexB = edge.twin.originatingVertex.index;
  return [vertIndexA, vertIndexB].toString();
}

function oppositeEdge(edge: HalfEdge): HalfEdge {
  const face = edge.face;
  const index = face.halfEdges.indexOf(edge);
  if (face.halfEdges.length === 4) {
    return face.halfEdges[(index + 2) % 4];
  }
  const edgeA = face.halfEdges[(index + 1) % 3];
  const edgeB = face.halfEdges[(index + 2) % 3];
  const lengthA = halfEdgeLength(edgeA);
  const lengthB = halfEdgeLength(edgeB);
  return lengthA > lengthB ? edgeA : edgeB;
}

export function halfEdgeMeshFromMesh3(mesh: Mesh3, throwOnNonManifoldEdges: boolean = true): HalfEdgeMesh {
  return halfEdgeMeshFromQuadMesh3({ ...mesh, quads: new Uint32Array([]) }, throwOnNonManifoldEdges);
}

/**
 * Assumes that the source mesh doesn't have any holes in it.
 * @param quadMesh
 * @returns
 */
export function halfEdgeMeshFromQuadMesh3(quadMesh: QuadMesh3, throwOnNonManifoldEdges: boolean = true): HalfEdgeMesh {
  const verts = quadMesh.verts;

  const halfEdgeVerts: Vertex[] = [];
  const halfEdgeFaces: HalfEdgeFace[] = [];
  const halfEdges: HalfEdge[] = [];

  for (let i = 0, l = verts.length; i < l; i += 3) {
    halfEdgeVerts.push({
      position: [verts[i], verts[i + 1], verts[i + 2]],
      index: i / 3,
    });
  }

  const edgeCount = new Map();
  const existingHalfedges: Map<string, Partial<HalfEdge>> = new Map();

  function addHalfEdges(indexArray: ArrayLike<number>, vertsPerPolygon: number) {
    for (let i = 0, l = indexArray.length; i < l; i += vertsPerPolygon) {
      const face: HalfEdgeFace = {
        halfEdges: [],
      };
      halfEdgeFaces.push(face);

      for (let j = 0; j < vertsPerPolygon; j++) {
        const vert = halfEdgeVerts[indexArray[i + j]];
        const newEdge: Partial<HalfEdge> = {
          originatingVertex: vert,
          face,
        };
        halfEdges.push(newEdge as HalfEdge);
        face.halfEdges.push(newEdge as HalfEdge);

        const k = (j + 1) % vertsPerPolygon;
        const vertIndexA = indexArray[i + j];
        const vertIndexB = indexArray[i + k];

        const edgeKey = [Math.min(vertIndexA, vertIndexB), Math.max(vertIndexA, vertIndexB)].toString();
        if (existingHalfedges.has(edgeKey)) {
          const twin = existingHalfedges.get(edgeKey);
          newEdge.twin = twin as HalfEdge;
          if (twin) {
            twin.twin = newEdge as HalfEdge;
            twin.otherVertex = undefined; // Unset since we found the twin.
          }

          edgeCount.set(edgeKey, edgeCount.get(edgeKey) + 1);
        } else {
          newEdge.otherVertex = halfEdgeVerts[indexArray[i + k]]; // We didn't find the twin yet, so include the other vertex for debugging.
          existingHalfedges.set(edgeKey, newEdge);
          edgeCount.set(edgeKey, 1);
        }

        if (edgeCount.get(edgeKey) > 2) {
          if (throwOnNonManifoldEdges) {
            throw "Mesh has non-manifold edges!";
          } else {
            newEdge.isNonManifold = true;
          }
        }
      }
    }
  }

  addHalfEdges(quadMesh.quads, 4);
  addHalfEdges(quadMesh.tris, 3);

  return {
    verts: halfEdgeVerts,
    faces: halfEdgeFaces,
  };
}

function mixVec3(a: number[], b: number[], t: number): number[] {
  return [a[0] * (1 - t) + b[0] * t, a[1] * (1 - t) + b[1] * t, a[2] * (1 - t) + b[2] * t];
}

/**
 * Subdivide a half-edge mesh in place.
 * @param halfEdgeMesh
 * @param maxEdgeLength Edges longer than this will be subdivided until their length is below maxEdgeLength.
 */
export function subdivideHalfEdgeMesh(halfEdgeMesh: HalfEdgeMesh, maxEdgeLength: number) {
  const verts = halfEdgeMesh.verts;

  // Sort the edges by length.
  const edgesToSubdivide: HalfEdge[] = [];
  for (const face of halfEdgeMesh.faces) {
    for (const halfEdge of face.halfEdges) {
      if (halfEdge.twin === undefined) {
        console.error("Unable to subdivide mesh - edge was missing its twin! " + halfEdge.originatingVertex.position);
        return;
      }
      if (halfEdge.isNonManifold) {
        console.error("Unable to subdivide mesh - non-manifold edge detected! " + halfEdge.originatingVertex.position);
        return;
      }
      const edgeLength = halfEdgeLength(halfEdge);
      if (edgeLength > maxEdgeLength) {
        edgesToSubdivide.push(halfEdge);
      }
    }
  }
  edgesToSubdivide.sort((a, b) => halfEdgeLength(b) - halfEdgeLength(a));

  // Map from original half-edges to half-edges added during subdivision.
  const subdividedEdges: Map<string, HalfEdge[]> = new Map();

  // Subdivide a single edge and find other edges that should be subdivided as a consequence.
  function subdivideEdge(edgeToSubdivide: HalfEdge, segmentCount: number, queuedEdges: HalfEdge[]) {
    if (subdividedEdges.has(halfEdgeIdentifier(edgeToSubdivide))) {
      return;
    }

    const twinEdge = edgeToSubdivide.twin;
    // TODO: Create new vertices in the middle of this half-edge, and add new half-edges and their twins to subdividedEdges.
    const startPos = edgeToSubdivide.originatingVertex.position;
    const endPos = edgeToSubdivide.twin.originatingVertex.position;

    const subdividedEdge: HalfEdge[] = [];

    let prevVertex = edgeToSubdivide.originatingVertex;
    for (let i = 0, newVerts = segmentCount - 1; i < newVerts; ++i) {
      const index = verts.length;
      const middleVertex = { position: mixVec3(startPos, endPos, (i + 1) / segmentCount), index };
      verts.push(middleVertex);
      const newEdge: Partial<HalfEdge> = { originatingVertex: prevVertex, face: edgeToSubdivide.face };
      const newTwin: HalfEdge = {
        originatingVertex: middleVertex,
        face: edgeToSubdivide.face,
        twin: newEdge as HalfEdge,
      };
      newEdge.twin = newTwin;
      subdividedEdge.push(newEdge as HalfEdge);
      prevVertex = middleVertex;
    }
    const newEdge: Partial<HalfEdge> = { originatingVertex: prevVertex, face: edgeToSubdivide.face };
    const newTwin: HalfEdge = {
      originatingVertex: twinEdge.originatingVertex,
      face: edgeToSubdivide.face,
      twin: newEdge as HalfEdge,
    };
    newEdge.twin = newTwin;
    subdividedEdge.push(newEdge as HalfEdge);

    if (!(subdividedEdge.length === segmentCount)) {
      console.assert(false);
    }

    subdividedEdges.set(halfEdgeIdentifier(edgeToSubdivide), subdividedEdge);
    subdividedEdges.set(halfEdgeIdentifier(twinEdge), subdividedEdge.map((edge) => edge.twin).reverse());

    // Find the edges that should be subdivided since they're on the same faces as other subdivided edges.
    // In case those edges have already been subdivided, they will be found from subdividedEdges and won't be processed again.
    const opposite = oppositeEdge(edgeToSubdivide);
    if (edgeToSubdivide.face.halfEdges.length === 4 || halfEdgeLength(opposite) > maxEdgeLength) {
      // The opposite edge should be subdivided as well. Queue it for processing with the same segmentCount.
      // If the face is a triangle and an opposite edge is shorter than maxEdgeLength, it's not split (the face can be split into a triangle fan).
      queuedEdges.push(opposite);
    }
    const twinOpposite = oppositeEdge(twinEdge);
    if (twinEdge.face.halfEdges.length === 4 || halfEdgeLength(twinOpposite) > maxEdgeLength) {
      queuedEdges.push(twinOpposite);
    }
  }

  for (const edgeToSubdivide of edgesToSubdivide) {
    if (subdividedEdges.has(halfEdgeIdentifier(edgeToSubdivide))) {
      continue;
    }

    const edgeLength = halfEdgeLength(edgeToSubdivide);
    const segmentCount: number = Math.ceil(edgeLength / maxEdgeLength);

    const queuedEdges: HalfEdge[] = [edgeToSubdivide];

    while (queuedEdges.length > 0) {
      const [subdivide] = queuedEdges.splice(0, 1);
      subdivideEdge(subdivide, segmentCount, queuedEdges);
    }
  }

  //console.log("Subdivided edge count " + subdividedEdges.size);

  const newFaces: HalfEdgeFace[] = [];

  function createInteriorVertexAndEdgeColumn(
    xCount: number,
    yCount: number,
    x: number,
    topEdges: HalfEdge[],
    bottomVertex: Vertex,
    bottomLeft: Vertex,
    bottomRight: Vertex,
    topLeft: Vertex,
    topRight: Vertex,
  ): Partial<HalfEdge>[] {
    // xCount and yCount correspond the columns and rows of faces inside the face being subdivided.
    let interiorBottomVertex = bottomVertex;
    const interiorRightEdgeColumn: Partial<HalfEdge>[] = [];
    for (let y = 0; y < yCount - 1; ++y) {
      const index = verts.length;
      const yT = (y + 1) / yCount;
      const interiorVertex: Vertex = {
        position: mixVec3(
          mixVec3(bottomLeft.position, topLeft.position, yT),
          mixVec3(bottomRight.position, topRight.position, yT),
          (x + 1) / xCount,
        ),
        index,
      };
      verts.push(interiorVertex);

      const edge: Partial<HalfEdge> = { originatingVertex: interiorBottomVertex };
      const edgeTwin: Partial<HalfEdge> = { originatingVertex: interiorVertex, twin: edge as HalfEdge };
      edge.twin = edgeTwin as HalfEdge;
      interiorRightEdgeColumn.push(edge);
      interiorBottomVertex = interiorVertex;
    }
    {
      const edge: Partial<HalfEdge> = { originatingVertex: interiorBottomVertex };
      const edgeTwin: Partial<HalfEdge> = {
        originatingVertex: topEdges[xCount - x - 1].originatingVertex,
        twin: edge as HalfEdge,
      };
      edge.twin = edgeTwin as HalfEdge;
      interiorRightEdgeColumn.push(edge);
    }

    return interiorRightEdgeColumn;
  }

  function createInteriorFaceColumn(
    xCount: number,
    yCount: number,
    x: number,
    interiorRightEdges: Partial<HalfEdge>[][],
    leftEdges: HalfEdge[],
    rightEdges: HalfEdge[],
    topEdges: HalfEdge[],
    bottomEdge: Partial<HalfEdge> | undefined,
  ) {
    let newFaceBottomEdge: Partial<HalfEdge> | undefined = bottomEdge;
    let newFaceRightEdges: Partial<HalfEdge>[] = rightEdges;
    let newFaceLeftEdges: (HalfEdge | undefined)[] = leftEdges;
    if (x < xCount - 1) {
      newFaceRightEdges = interiorRightEdges[x];
    }
    if (x > 0) {
      newFaceLeftEdges = interiorRightEdges[x - 1].map((e) => e.twin).reverse();
    }
    for (let y = 0; y < yCount - 1; ++y) {
      const rightEdge = newFaceRightEdges[y];
      const leftEdge = ensureDefined(newFaceLeftEdges[yCount - y - 1]);
      const newTopEdge: Partial<HalfEdge> = { originatingVertex: rightEdge.twin?.originatingVertex };
      let newFace: HalfEdgeFace;
      if (newFaceBottomEdge !== undefined) {
        newFace = {
          halfEdges: [
            newFaceBottomEdge as HalfEdge,
            rightEdge as HalfEdge,
            newTopEdge as HalfEdge,
            leftEdge as HalfEdge,
          ],
        };
        newFaceBottomEdge.face = newFace;
      } else {
        newFace = {
          halfEdges: [rightEdge as HalfEdge, newTopEdge as HalfEdge, leftEdge as HalfEdge],
        };
      }
      newFaces.push(newFace);
      rightEdge.face = newFace;
      leftEdge.face = newFace;
      newTopEdge.face = newFace;
      newFaceBottomEdge = { originatingVertex: leftEdge.originatingVertex, twin: newTopEdge as HalfEdge };
      newTopEdge.twin = newFaceBottomEdge as HalfEdge;
    }
    {
      const finalTopEdge: HalfEdge = topEdges[xCount - x - 1];
      const rightEdge: Partial<HalfEdge> = newFaceRightEdges[yCount - 1];
      const leftEdge: Partial<HalfEdge> = ensureDefined(newFaceLeftEdges[0]);
      const topFace: HalfEdgeFace = {
        halfEdges: [newFaceBottomEdge as HalfEdge, rightEdge as HalfEdge, finalTopEdge, leftEdge as HalfEdge],
      };
      newFaces.push(topFace);
      if (newFaceBottomEdge) {
        newFaceBottomEdge.face = topFace;
      }
      rightEdge.face = topFace;
      leftEdge.face = topFace;
      finalTopEdge.face = topFace;
    }
  }

  for (const face of halfEdgeMesh.faces) {
    let splitEdgeCount: number = 0;
    let firstSplitEdgeIndex: number | undefined = undefined;

    let edgeIndex = 0;
    for (const halfEdge of face.halfEdges) {
      const edgeKey = halfEdgeIdentifier(halfEdge);
      if (subdividedEdges.has(edgeKey)) {
        ++splitEdgeCount;
        if (firstSplitEdgeIndex === undefined) {
          firstSplitEdgeIndex = edgeIndex;
        }
      }
      ++edgeIndex;
    }
    if (splitEdgeCount === 0) {
      newFaces.push(face);
      continue;
    }
    if (face.halfEdges.length === 4) {
      // Subdivide quad
      if (splitEdgeCount === 4) {
        // Subdivide the quad into a regular grid.
        const bottomEdges = ensureDefined(subdividedEdges.get(halfEdgeIdentifier(face.halfEdges[0])));
        const rightEdges = ensureDefined(subdividedEdges.get(halfEdgeIdentifier(face.halfEdges[1])));
        const topEdges = ensureDefined(subdividedEdges.get(halfEdgeIdentifier(face.halfEdges[2])));
        const leftEdges = ensureDefined(subdividedEdges.get(halfEdgeIdentifier(face.halfEdges[3])));
        if (!(leftEdges.length === rightEdges.length)) {
          console.assert(false);
        }
        if (!(bottomEdges.length === topEdges.length)) {
          console.assert(false);
        }
        const xCount = bottomEdges.length;
        const yCount = leftEdges.length;
        const interiorRightEdges: Partial<HalfEdge>[][] = [];
        const bottomLeft = bottomEdges[0].originatingVertex;
        const bottomRight = rightEdges[0].originatingVertex;
        const topRight = topEdges[0].originatingVertex;
        const topLeft = leftEdges[0].originatingVertex;
        // Create at grid of interior vertices and vertical edges.
        for (let x = 0; x < xCount - 1; ++x) {
          const bottomVertex = bottomEdges[x + 1].originatingVertex;
          const interiorRightEdgeColumn: Partial<HalfEdge>[] = createInteriorVertexAndEdgeColumn(
            xCount,
            yCount,
            x,
            topEdges,
            bottomVertex,
            bottomLeft,
            bottomRight,
            topLeft,
            topRight,
          );
          interiorRightEdges.push(interiorRightEdgeColumn);
        }
        // Create all horizontal edges and quads.
        for (let x = 0; x < xCount; ++x) {
          createInteriorFaceColumn(
            xCount,
            yCount,
            x,
            interiorRightEdges,
            leftEdges,
            rightEdges,
            topEdges,
            bottomEdges[x],
          );
        }
      } else {
        if (!(splitEdgeCount === 2)) {
          console.assert(false);
        }
        // Create new faces to replace the old one, and push to newFaces.
        const bottomEdgeIndex = subdividedEdges.has(halfEdgeIdentifier(face.halfEdges[0])) ? 1 : 0;
        const rightEdges: HalfEdge[] = ensureDefined(
          subdividedEdges.get(halfEdgeIdentifier(face.halfEdges[(bottomEdgeIndex + 1) % 4])),
        );
        const leftEdges: HalfEdge[] = ensureDefined(
          subdividedEdges.get(halfEdgeIdentifier(face.halfEdges[(bottomEdgeIndex + 3) % 4])),
        );
        if (!(leftEdges.length === rightEdges.length)) {
          console.assert(false);
        }
        const newQuadCount = rightEdges.length;

        const finalTopEdge: HalfEdge = face.halfEdges[(bottomEdgeIndex + 2) % 4];
        let newFaceBottomEdge: Partial<HalfEdge> = face.halfEdges[bottomEdgeIndex];
        for (let i = 0; i < newQuadCount - 1; ++i) {
          const rightEdge: HalfEdge = rightEdges[i];
          const leftEdge: HalfEdge = leftEdges[newQuadCount - i - 1];
          const newTopEdge: Partial<HalfEdge> = { originatingVertex: rightEdge.twin.originatingVertex };
          const newFace: HalfEdgeFace = {
            halfEdges: [newFaceBottomEdge as HalfEdge, rightEdge, newTopEdge as HalfEdge, leftEdge],
          };
          newFaces.push(newFace);
          newFaceBottomEdge.face = newFace;
          rightEdge.face = newFace;
          leftEdge.face = newFace;
          newTopEdge.face = newFace;
          newFaceBottomEdge = { originatingVertex: leftEdge.originatingVertex, twin: newTopEdge as HalfEdge };
          newTopEdge.twin = newFaceBottomEdge as HalfEdge;
        }
        {
          const rightEdge: HalfEdge = rightEdges[newQuadCount - 1];
          const leftEdge: HalfEdge = leftEdges[0];
          const topFace: HalfEdgeFace = {
            halfEdges: [newFaceBottomEdge as HalfEdge, rightEdge, finalTopEdge, leftEdge],
          };
          newFaces.push(topFace);
          newFaceBottomEdge.face = topFace;
          rightEdge.face = topFace;
          leftEdge.face = topFace;
          finalTopEdge.face = topFace;
        }
      }
    } else {
      const segmentCounts: number[] = [];
      if (splitEdgeCount > 1) {
        for (const halfEdge of face.halfEdges) {
          const edgeKey = halfEdgeIdentifier(halfEdge);
          if (subdividedEdges.has(edgeKey)) {
            segmentCounts.push(ensureDefined(subdividedEdges.get(edgeKey)).length);
          }
        }
      }
      let segmentCountsMatch = true;
      if (segmentCounts.length === 2 && segmentCounts[0] !== segmentCounts[1]) {
        segmentCountsMatch = false;
      }
      if (
        segmentCounts.length === 3 &&
        segmentCounts[0] !== segmentCounts[1] &&
        segmentCounts[1] !== segmentCounts[2] &&
        segmentCounts[0] !== segmentCounts[2]
      ) {
        segmentCountsMatch = false;
      }
      // Subdivide triangle.
      if (splitEdgeCount === 1) {
        // Only one of the edges is split. Split the face into a triangle fan.
        const bottomEdges = ensureDefined(
          subdividedEdges.get(halfEdgeIdentifier(face.halfEdges[ensureDefined(firstSplitEdgeIndex)])),
        );
        let rightEdge: Partial<HalfEdge> = face.halfEdges[(ensureDefined(firstSplitEdgeIndex) + 1) % 3];
        const lastLeftEdge = face.halfEdges[(ensureDefined(firstSplitEdgeIndex) + 2) % 3];
        for (let i = 0; i < bottomEdges.length - 1; ++i) {
          const bottomEdge = bottomEdges[i];
          const newLeftEdge: Partial<HalfEdge> = { originatingVertex: lastLeftEdge.originatingVertex };
          const newFace: HalfEdgeFace = {
            halfEdges: [bottomEdge, rightEdge as HalfEdge, newLeftEdge as HalfEdge],
          };
          newFaces.push(newFace);
          bottomEdges[i].face = newFace;
          rightEdge.face = newFace;
          newLeftEdge.face = newFace;
          rightEdge = { originatingVertex: bottomEdge.originatingVertex, twin: newLeftEdge as HalfEdge };
          newLeftEdge.twin = rightEdge as HalfEdge;
        }
        const newFace: HalfEdgeFace = {
          halfEdges: [bottomEdges[bottomEdges.length - 1], rightEdge as HalfEdge, lastLeftEdge as HalfEdge],
        };
        newFaces.push(newFace);
        bottomEdges[bottomEdges.length - 1].face = newFace;
        rightEdge.face = newFace;
        lastLeftEdge.face = newFace;
      } else if (!segmentCountsMatch) {
        // In case the segment counts don't match, then create one interior vertex at the centroid and connect the split edges to it with triangle fans.
        const centroid = [0, 0, 0];
        for (let dim = 0; dim < 3; ++dim) {
          for (const halfEdge of face.halfEdges) {
            centroid[dim] += halfEdge.originatingVertex.position[dim];
          }
          centroid[dim] = centroid[dim] / 3;
        }
        const index = verts.length;
        const centroidVertex = { position: centroid, index };
        verts.push(centroidVertex);
        const firstLeftEdge: Partial<HalfEdge> = { originatingVertex: centroidVertex };
        let leftEdge: Partial<HalfEdge> = firstLeftEdge;
        for (let originalEdgeIndex = 0; originalEdgeIndex < 3; ++originalEdgeIndex) {
          const originalHalfEdge = face.halfEdges[originalEdgeIndex];
          const bottomEdges = ensureDefined(subdividedEdges.get(halfEdgeIdentifier(originalHalfEdge)));
          for (
            let bottomEdgeIndex = 0, bottomEdgeCount = bottomEdges.length;
            bottomEdgeIndex < bottomEdgeCount;
            ++bottomEdgeIndex
          ) {
            const bottomEdge = bottomEdges[bottomEdgeIndex];
            const newRightEdge: Partial<HalfEdge> = { originatingVertex: bottomEdge.twin.originatingVertex };
            const newFace: HalfEdgeFace = {
              halfEdges: [leftEdge as HalfEdge, bottomEdge, newRightEdge as HalfEdge],
            };
            newFaces.push(newFace);
            bottomEdge.face = newFace;
            leftEdge.face = newFace;
            newRightEdge.face = newFace;
            const isLastTri = originalEdgeIndex === 2 && bottomEdgeIndex === bottomEdgeCount - 1;
            leftEdge = isLastTri ? firstLeftEdge : { originatingVertex: centroidVertex };
            newRightEdge.twin = leftEdge as HalfEdge;
            leftEdge.twin = newRightEdge as HalfEdge;
          }
        }
      } else if (splitEdgeCount === 2) {
        // Two edges are split. Split the face into strips (one triangle and the rest quads).
        edgeIndex = 0;
        let secondSplitEdgeIndex: number | undefined;
        for (const halfEdge of face.halfEdges) {
          const edgeKey = halfEdgeIdentifier(halfEdge);
          if (subdividedEdges.has(edgeKey) && edgeIndex !== ensureDefined(firstSplitEdgeIndex)) {
            secondSplitEdgeIndex = edgeIndex;
          }
          ++edgeIndex;
        }

        if (!(ensureDefined(secondSplitEdgeIndex) !== undefined &&
            ensureDefined(secondSplitEdgeIndex) > ensureDefined(firstSplitEdgeIndex))) {
          console.assert(false);
        }

        if (ensureDefined(secondSplitEdgeIndex) === (ensureDefined(firstSplitEdgeIndex) + 2) % 3) {
          [secondSplitEdgeIndex, firstSplitEdgeIndex] = [ensureDefined(firstSplitEdgeIndex), secondSplitEdgeIndex];
        }
        const leftEdges: HalfEdge[] = ensureDefined(
          subdividedEdges.get(halfEdgeIdentifier(face.halfEdges[ensureDefined(firstSplitEdgeIndex)])),
        );
        const rightEdges: HalfEdge[] = ensureDefined(
          subdividedEdges.get(halfEdgeIdentifier(face.halfEdges[ensureDefined(secondSplitEdgeIndex)])),
        );
        let topEdge: Partial<HalfEdge> = face.halfEdges[(ensureDefined(secondSplitEdgeIndex) + 1) % 3];
        if (!(leftEdges.length === rightEdges.length)) {
          console.assert(false);
        }

        const quadCount = leftEdges.length - 1;
        for (let i = 0; i < quadCount; ++i) {
          const newBottomEdge: Partial<HalfEdge> = { originatingVertex: leftEdges[i + 1].originatingVertex };
          const leftEdge = leftEdges[i];
          const rightEdge = rightEdges[rightEdges.length - 1 - i];
          const newFace: HalfEdgeFace = {
            halfEdges: [topEdge as HalfEdge, leftEdge, newBottomEdge as HalfEdge, rightEdge],
          };
          newFaces.push(newFace);
          newBottomEdge.face = newFace;
          topEdge.face = newFace;
          leftEdge.face = newFace;
          rightEdge.face = newFace;
          topEdge = { originatingVertex: rightEdge.originatingVertex, twin: newBottomEdge as HalfEdge };
          newBottomEdge.twin = topEdge as HalfEdge;
        }
        const triLeftEdge: HalfEdge = leftEdges[leftEdges.length - 1];
        const triRightEdge = rightEdges[0];
        const newTri: HalfEdgeFace = { halfEdges: [topEdge as HalfEdge, triLeftEdge, triRightEdge] };
        newFaces.push(newTri);
        topEdge.face = newTri;
        triLeftEdge.face = newTri;
        triRightEdge.face = newTri;
      } else if (splitEdgeCount === 3) {
        // Split the face into a grid of quads terminated by a triangle fan at the "bottom" vertex.
        if (!(segmentCounts.length === 3)) {
          console.assert(false);
        }
        let leftEdgeIndex = 0;
        let rightEdgeIndex = 1;
        let topEdgeIndex = 2;
        if (segmentCounts[1] === segmentCounts[2]) {
          leftEdgeIndex = 1;
          rightEdgeIndex = 2;
          topEdgeIndex = 0;
        } else if (segmentCounts[0] === segmentCounts[2]) {
          leftEdgeIndex = 2;
          rightEdgeIndex = 0;
          topEdgeIndex = 1;
        }
        if (!(segmentCounts[leftEdgeIndex] === segmentCounts[rightEdgeIndex])) {
          console.assert(false);
        }
        const leftEdges: HalfEdge[] = ensureDefined(
          subdividedEdges.get(halfEdgeIdentifier(face.halfEdges[leftEdgeIndex])),
        );
        const rightEdges: HalfEdge[] = ensureDefined(
          subdividedEdges.get(halfEdgeIdentifier(face.halfEdges[rightEdgeIndex])),
        );
        const topEdges: HalfEdge[] = ensureDefined(
          subdividedEdges.get(halfEdgeIdentifier(face.halfEdges[topEdgeIndex])),
        );
        if (!(leftEdges.length === rightEdges.length)) {
          console.assert(false);
        }

        const yCount = leftEdges.length;
        const xCount = topEdges.length;
        // Create at grid of interior vertices and vertical edges.
        const interiorRightEdges: Partial<HalfEdge>[][] = [];
        const bottom = rightEdges[0].originatingVertex;
        const topRight = topEdges[0].originatingVertex;
        const topLeft = leftEdges[0].originatingVertex;
        for (let x = 0; x < xCount - 1; ++x) {
          const bottomVertex = rightEdges[0].originatingVertex;
          const interiorRightEdgeColumn = createInteriorVertexAndEdgeColumn(
            xCount,
            yCount,
            x,
            topEdges,
            bottomVertex,
            bottom,
            bottom,
            topLeft,
            topRight,
          );
          interiorRightEdges.push(interiorRightEdgeColumn);
        }
        // Create all horizontal edges and the faces.
        for (let x = 0; x < xCount; ++x) {
          createInteriorFaceColumn(xCount, yCount, x, interiorRightEdges, leftEdges, rightEdges, topEdges, undefined);
        }
      }
    }
  }

  halfEdgeMesh.faces = newFaces;
}

export function quadMesh3FromHalfEdgeMesh(halfEdgeMesh: HalfEdgeMesh): QuadMesh3 {
  const verts: number[] = [];
  const tris: number[] = [];
  const quads: number[] = [];
  for (let i = 0, l = halfEdgeMesh.verts.length; i < l; ++i) {
    const position = halfEdgeMesh.verts[i].position;
    verts.push(...position);
  }
  for (const face of halfEdgeMesh.faces) {
    if (face.halfEdges.length === 4) {
      for (const halfEdge of face.halfEdges) {
        quads.push(halfEdge.originatingVertex.index);
      }
    } else {
      if (!(face.halfEdges.length === 3)) {
        console.assert(false);
      }
      for (const halfEdge of face.halfEdges) {
        tris.push(halfEdge.originatingVertex.index);
      }
    }
  }
  return quadMesh3New(new Float32Array(verts), new Uint32Array(tris), new Uint32Array(quads));
}
